import { observer } from 'mobx-react-lite';
import React from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * 첫 번째 컬럼은 왼쪽 정렬, 나머지는 오른쪽 정렬
 */
const TableHeader = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        return (
            <th
                className={twMerge(
                    'text-base-content/70 px-2 py-2 text-right text-sm font-normal first-of-type:pl-1 first-of-type:text-left last-of-type:pr-1',
                    className,
                )}
            >
                {children}
            </th>
        );
    },
);

const TableRow = observer(
    ({
        children,
        className,
        onClick,
    }: {
        children: React.ReactNode;
        className?: string;
        onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
    }) => {
        return (
            <tr className={twMerge(className)} onClick={onClick}>
                {children}
            </tr>
        );
    },
);

/**
 * 첫 번째 컬럼은 왼쪽 정렬, 나머지는 오른쪽 정렬
 */
const TableCell = observer(
    ({
        children,
        className,
        rowSpan,
        colSpan,
    }: {
        children: React.ReactNode;
        className?: string;
        rowSpan?: number;
        colSpan?: number;
    }) => {
        return (
            <td
                className={twMerge(
                    'px-2 py-2 text-right whitespace-nowrap first-of-type:pl-1 first-of-type:text-left first-of-type:whitespace-normal last-of-type:pr-1',
                    className,
                )}
                {...(rowSpan && { rowSpan })}
                {...(colSpan && { colSpan })}
            >
                {children}
            </td>
        );
    },
);

const Table = Object.assign(
    observer(
        ({
            children,
            fixed,
            className,
        }: {
            fixed?: boolean;
            children: React.ReactNode;
            className?: string;
        }) => {
            return (
                <div className={twMerge('overflow-x-auto', className)}>
                    <table className={twMerge('table', fixed && 'table-fixed')}>
                        <thead>
                            <tr>
                                {React.Children.map(children, (child) => {
                                    if (!React.isValidElement(child)) {
                                        return child;
                                    }

                                    if (child.type === Table.Header) {
                                        return child;
                                    }

                                    return null;
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {React.Children.map(children, (child) => {
                                if (!React.isValidElement(child)) {
                                    return child;
                                }

                                if (child.type === Table.Row) {
                                    return child;
                                } else if (child.type === Table.Header) {
                                    return null;
                                }

                                return child;
                            })}
                        </tbody>
                    </table>
                </div>
            );
        },
    ),
    {
        Header: TableHeader,
        Row: TableRow,
        Cell: TableCell,
    },
);

export default Table;
